import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLicenseStore } from './stores/license';
import { useCompanyStore } from './stores/company';
import { useAiPanelStore } from '@/store/aiPanelStore';
import { API } from '@/lib/apiConfig';
import LoadingSpinner from './components/common/LoadingSpinner';
import ErrorBanner from './components/common/ErrorBanner';

async function fetchJsonWithRetry(url: string, init: RequestInit, attempts = 3, backoffMs = 400): Promise<any> {
  let lastErr: any;
  for (let i = 0; i < attempts; i++) {
    try {
      const res = await fetch(url, init);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (e) {
      lastErr = e;
      await new Promise((r) => setTimeout(r, backoffMs * (i + 1)));
    }
  }
  throw lastErr;
}

export function LicenseKey() {
  const { t } = useTranslation();
  const { licenseKey, valid, validateAndSave } = useLicenseStore();
  const activeCompany = useCompanyStore((s) => s.companies.find((c) => c.id === s.activeId) ?? null);
  const [input, setInput] = useState<string>(licenseKey ?? '');
  const [touched, setTouched] = useState(false);
  const [onlineStatus, setOnlineStatus] = useState<'unknown' | 'active' | 'revoked' | 'offline'>('unknown');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const onSave = async () => {
    const key = input.trim();
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchJsonWithRetry(API.license + '/activate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ licenseKey: key, companyId: activeCompany?.id || 'default' })
      });
      setOnlineStatus(resp.status === 'active' ? 'active' : 'revoked');
      validateAndSave(key);
      if (resp.status === 'active') {
        useAiPanelStore.getState().setEnabled(true);
      }
      setTouched(true);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to activate license');
      setError(error);
      setOnlineStatus('offline');
      validateAndSave(key); // store locally but UI will remain disabled unless online active
      setTouched(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // On mount or license change, check online status
    (async () => {
      const key = (licenseKey || '').trim();
      if (!key) return;
      setLoading(true);
      setError(null);
      try {
        const resp = await fetchJsonWithRetry(`${API.license}/status?licenseKey=${encodeURIComponent(key)}`, { method: 'GET' });
        setOnlineStatus(resp.status === 'active' ? 'active' : 'revoked');
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to check license status');
        setError(error);
        setOnlineStatus('offline');
      } finally {
        setLoading(false);
      }
    })();
  }, [licenseKey]);

  return (
    <div className="p-4 border border-evat-gold/40 rounded bg-black/50 space-y-3">
      <div className="mb-2 font-orbitron text-evat-gold">{t('license.title') || 'License Key'}</div>
      
      <ErrorBanner error={error} onRetry={onSave} showRetry={!!error} />
      
      <div className="flex gap-2 items-center">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t('license.placeholder') || 'EVAT-XXXX-XXXX'}
          className="flex-1 bg-black text-neutral-100 border border-evat-neon rounded px-3 py-2 focus:outline-none focus:shadow-neon"
          disabled={loading}
        />
        <button 
          onClick={onSave} 
          disabled={loading}
          className="px-3 py-2 border border-evat-neon text-evat-neon rounded hover:shadow-neon disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? '...' : (t('license.save') || 'Save')}
        </button>
      </div>
      
      {loading && <LoadingSpinner size="sm" text={t('license.checking') || 'Checking license...'} />}
      
      {touched && !loading && (
        <div className={`mt-2 text-sm ${onlineStatus === 'active' ? 'text-evat-neon' : onlineStatus === 'offline' ? 'text-yellow-400' : 'text-red-400'}`}>
          {onlineStatus === 'active' ? (t('license.valid') || 'Valid') : onlineStatus === 'offline' ? (t('license.offline') || 'Offline: validation pending') : (t('license.invalid') || 'Invalid license key')}
        </div>
      )}
    </div>
  );
}

export default LicenseKey;


